<template>
  <BasicBlock
    :title="$t('result.block.title')"
    :id="id"
    class="fapi-form-result-container"
  >
    <div
      class="fapi-form-result-prices-group f-flex f-flex-col sm:f-flex-row f-justify-between"
    >
      <div>
        <DiscountCode
          class="fapi-form-result-discount-code"
          v-if="formResource.allow_discount_codes"
          :state="discountCodeState"
          :isProcessing="isProcessing"
          @discountCodeChanged="$emit('discountCodeChanged', $event)"
          @tryToApplyDiscountCode="$emit('tryToApplyDiscountCode')"
        ></DiscountCode>

        <Repayment
          class="fapi-form-result-repayment"
          v-if="formResource.allow_repayments"
          @repaymentChanged="$emit('repaymentChanged', $event)"
          @installmentsChanged="$emit('installmentsChanged', $event)"
          :repaymentCountOptions="formResource.repayment_counts || [2]"
          :isChangeNameRepaymentToPayment="isChangeNameRepaymentToPayment"
        ></Repayment>
      </div>

      <Prices
        class="fapi-form-result-prices"
        :order="order"
        :discountCode="discountCode"
        :repayment="repayment"
        :repaymentCount="repaymentCount"
        :currency="currency"
        :showPricesIncludingVat="formResource.show_prices_including_vat"
        :isChangeNameRepaymentToPayment="isChangeNameRepaymentToPayment"
        :installments="installments"
      ></Prices>
    </div>

    <p
      v-if="formResource.require_recurring_terms || formResource.allow_terms"
      class="fapi-form-result-terms f-mb-2"
    >
      <Terms
        :formResource="formResource"
        @termsChanged="$emit('termsChanged', $event)"
      >
      </Terms>
    </p>

    <p
      v-for="(purpose, index) in purposes"
      v-bind:key="index + '-purpose'"
      class="fapi-form-result-purposes f-mb-2"
    >
      <Purpose
        :purpose="purpose"
        @purposeChanged="$emit('purposeChanged', $event)"
      ></Purpose>
    </p>

    <p
      v-for="(primaryPurpose, index) in primaryPurposes"
      v-bind:key="index + '-primary-purpose'"
      class="fapi-form-result-primary-purposes f-mb-2"
    >
      <PrimaryPurpose
        :purpose="primaryPurpose"
        @purposeChanged="$emit('purposeChanged', $event)"
      ></PrimaryPurpose>
    </p>

    <div
      class="fapi-form-result-submit-group f-text-center f-w-full f-mt-4 f-pt-5"
      :id="$id('submit-group')"
    >
      <button
        type="submit"
        :id="$id('submit-button')"
        class="fapi-submit-button focus:f-ring-4"
        :disabled="isSubmitting || hasLoadingError"
        v-show="!isSubmitting && !hasLoadingError"
      >
        <span
          :aria-label="`${submitLabel}, celkem: ${order.totalPriceIncludingVat}${currency}`"
        >
          {{ submitLabel }}
        </span>
      </button>
      <div
        class="fapi-form-result-submit-group-submitting f-text-center f-mt-2 fapi-sending"
        v-if="isSubmitting"
      >
        <svg
          class="f-animate-spin f-h-11 f-w-11 f-mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="f-opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="f-opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="f-mt-2 fapi-sending">{{ $t("result.submit.processing") }}</p>
      </div>
    </div>
  </BasicBlock>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import BasicBlock from "@/components/Form/BasicBlock.vue";
import FormResource from "@/module/Form/Resources/FormResource";
import PrimaryPurpose from "@/components/Form/Result/PrimaryPurpose.vue";
import Terms from "@/components/Form/Result/Terms.vue";
import Purpose from "@/components/Form/Result/Purpose.vue";
import FormData from "@/module/FormData";
import Prices from "@/components/Form/Result/Prices.vue";
import OrderPrices from "@/module/Order/OrderPrices";
import Repayment from "@/components/Form/Result/Repayment.vue";
import DiscountCode from "@/components/Form/Result/DiscountCode.vue";
import {
  isBooleanOrNull,
  isCssStyleOrNull,
  isFapiDiscountCodeOrNull,
} from "@/module/ComponentPropsValidations/validations";
import CssStyle from "@/module/Form/Resources/CssStyle";
import EventBus from "@/module/Utils/EventBus";

interface ComponentData {
  code: string | null;
  primaryPurposes: Array<object>;
  purposes: Array<object>;
  id: string;
  hasLoadingError: boolean;
}

export default Vue.extend({
  name: "Result",

  components: {
    Prices,
    Repayment,
    DiscountCode,
    Purpose,
    PrimaryPurpose,
    Terms,
    BasicBlock,
  },

  props: {
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    styling: {
      type: null as unknown as PropType<CssStyle | null>,
      required: true,
      validator: isCssStyleOrNull,
    },
    order: {
      type: Object as PropType<OrderPrices>,
      required: true,
    },
    formData: {
      type: Object as PropType<FormData>,
      required: true,
    },
    isSubmitting: {
      type: Boolean,
      required: true,
    },
    discountCodeState: {
      required: true,
      validator: isBooleanOrNull,
    },
    isProcessing: {
      type: Boolean,
      required: true,
    },
    discountCode: {
      required: true,
      validator: isFapiDiscountCodeOrNull,
    },
    repayment: {
      type: Boolean,
      required: true,
    },
    repaymentCount: {
      type: Number,
      required: true,
    },
    installments: {
      type: Array as PropType<InstallmentItem[]>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    isChangeNameRepaymentToPayment: {
      type: Boolean,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      code: null,
      primaryPurposes: [],
      purposes: [],
      id: this.$id("result-container"),
      hasLoadingError: false,
    };
  },

  computed: {
    submitLabel(): string {
      if (
        this.styling === null ||
        !this.styling.enabled ||
        !this.styling.button_label
      ) {
        return this.$t("result.submit.label").toString();
      }

      return (
        this.styling.button_label || this.$t("result.submit.label").toString()
      );
    },
  },

  mounted(): void {
    for (const item of this.formResource.purposes) {
      if (item.is_primary) {
        this.primaryPurposes.push(item);
      } else if (item.step === "order") {
        this.purposes.push(item);
      }
    }

    this.$emit("resultContainerIdAssigned", { id: this.id });

    EventBus.$on("critical-component-failed", () => {
      this.hasLoadingError = true;
    });
  },
});
</script>

<style lang="scss">
.fapi-order-form {
  .fapi-submit-button {
    @apply f-py-3 f-px-6 f-transition-opacity f-duration-150 hover:f-opacity-80 focus:f-outline-none;
    font-size: var(--submit-button-font-size);
    color: var(--submit-button-text-color);
    border-radius: var(--submit-button-border-radius);
    background-color: var(--submit-button-background-color);
    font-family: var(--submit-button-font-family);
    line-height: 1em;
  }

  .fapi-sending {
    color: var(--submit-button-background-color);
    font-family: var(--submit-button-font-family);
  }
}
</style>
